import { EVENT_LAZYLOAD_LOADED, EVENT_RESIZE, Splide } from "@splidejs/splide";
import { VERBOSE } from "../config.js";

/** @typedef {import('@splidejs/splide').Options} SplideOptions */

export function createZoomAwareTransition(parent) {
  return (_Splide, Components, options) => {
    const {
      Move,
      Elements: { list },
    } = Components;
    let endCallback;

    const handleTransitionEnd = (event) => {
      if (event.target !== list || !endCallback) return;

      const callback = endCallback;
      cancel();
      callback();
    };

    const cancel = () => {
      list.style.transition = "";
      endCallback = undefined;
    };

    const mount = () => {
      list.addEventListener("transitionend", handleTransitionEnd);
    };

    const destroy = () => {
      cancel();
      list.removeEventListener("transitionend", handleTransitionEnd);
    };

    const start = (index, done) => {
      const zoomController = parent.zoomController;
      if (zoomController?.isZoomed()) {
        const ownsSuspendedTransform = zoomController.suspendTransform();

        try {
          // Splide derives the target translation from DOMRects.
          // Measure and position without Panzoom's outer transform;
          // page changes while zoomed are intentionally immediate.
          Move.jump(index);
          done();
        } finally {
          if (ownsSuspendedTransform) zoomController.restoreTransform();
        }
        return;
      }

      const destination = Move.toPosition(index, true);
      const position = Move.getPosition();

      if (Math.abs(destination - position) >= 1 && options.speed >= 1) {
        list.style.transition = `transform ${options.speed}ms ${options.easing}`;
        Move.translate(destination, true);
        endCallback = done;
      } else {
        Move.jump(index);
        done();
      }
    };

    return { mount, start, cancel, destroy };
  };
}

export default class SplideHandler {
  static doublePageClass = "doublePages";
  static navigationStateChangedEvent = "splidehandler:navigationstatechanged";
  static pageChangedEvent = "splidehandler:pagechanged";

  /**
   * @typedef SplideHandlerOptions
   * @property {boolean} verbose
   * @property {HTMLElement} root
   * @property {HTMLElement} book
   * @property {HTMLElement} container
   * @property {string} [classPrefix]
   * @property {SplideOptions} splideOptions
   */

  /**
   * @type SplideHandlerOptions
   */
  #options;

  /**
   * @type {Splide}
   */
  #splide;

  #zoomMeasurementPending = false;

  #windowResizeFrame;

  #window;

  #urlHashEnabled = false;

  #preferredPageHash = "";

  #pinchActive = false;

  #pinchReleaseFrame;

  #lastNavigationState;

  #isDualPage = false;

  #SplideClass;

  /**
   * @type RheaBookViewer
   */
  parent;

  /**
   * @type string
   */
  #classPrefix;

  /**
   *
   * @param {SplideHandlerOptions} options
   * @param {RheaBookViewer} parent
   */
  constructor(options, parent, { SplideClass = Splide } = {}) {
    if (options.verbose || VERBOSE)
      console.log("SplideHandler: new SplideHandler", this);
    this.parent = parent;
    this.#options = options;
    this.#SplideClass = SplideClass;
    this.#classPrefix = options.classPrefix ? options.classPrefix + "-" : "";
    this.#window = options.root.ownerDocument.defaultView ?? globalThis.window;
    this.#urlHashEnabled = Boolean(options.splideOptions.urlHash);
  }

  init() {
    if (this.#options.verbose || VERBOSE) console.log("SplideHandler: init");
    const startsInDualPageMode = this.#options.splideOptions.perPage === 2;

    // Boundary slides must exist before resolving a page hash because they
    // shift the real page indices in dual-page mode.
    if (startsInDualPageMode) {
      this.#isDualPage = true;
      this.#options.container.classList.add(SplideHandler.doublePageClass);
      this.#addInitialBoundarySlides();
    }

    const splideOptions = { ...this.#options.splideOptions };
    if (this.#urlHashEnabled) {
      this.#preferredPageHash = this.#getLocationHash();
      const pageIndex = this.#findPageIndexByHash(this.#preferredPageHash);
      if (pageIndex >= 0) {
        splideOptions.start = startsInDualPageMode
          ? this.#dualPageIndex(pageIndex)
          : pageIndex;
      }
    }

    this.#splide = new this.#SplideClass(this.#options.root, splideOptions);

    this.#options.root.addEventListener(
      "touchmove",
      this.#blockPinchTouchMove,
      {
        capture: true,
        passive: false,
      },
    );

    // Register before Splide mounts its throttled window resize listener.
    // This lets us remove the outer Panzoom transform in the preceding
    // animation-frame callback before Splide reads its scaled DOMRects.
    this.#window.addEventListener(
      "resize",
      this.#prepareWindowResizeMeasurement,
    );

    this.#splide.mount({}, createZoomAwareTransition(this.parent));

    this.#hookEvents();

    if (startsInDualPageMode) {
      this.#setAspectRatio();
    }
    this.#emitNavigationState();
  }

  #addInitialBoundarySlides() {
    const list = this.#options.root.querySelector(".splide__list");
    const createBoundarySlide = () => {
      const slide = document.createElement("li");
      const interior = document.createElement("div");

      slide.classList.add("splide__slide");
      interior.classList.add(this.#classPrefix + "page__interior");
      slide.append(interior);

      return slide;
    };

    list.prepend(createBoundarySlide());
    list.append(createBoundarySlide());
  }

  getNavigationState() {
    const arrows = this.#splide.Components.Arrows?.arrows;
    const canGoPrevious = arrows?.prev
      ? !arrows.prev.disabled
      : this.#splide.index > 0;
    const canGoNext = arrows?.next
      ? !arrows.next.disabled
      : this.#splide.index < this.#splide.length - 1;

    return Object.freeze({
      index: this.#splide.index,
      canGoPrevious,
      canGoNext,
      isFirst: !canGoPrevious,
      isLast: !canGoNext,
    });
  }

  #hookEvents() {
    if (this.#options.verbose || VERBOSE)
      console.log("SplideHandler: hookEvents");
    const firstPageImage = this.#options.root.querySelector("img");
    if (firstPageImage && !firstPageImage.complete) {
      firstPageImage.addEventListener("load", () => this.#setAspectRatio(), {
        once: true,
      });
    }

    this.#splide.on("moved", () => {
      if (this.#options.verbose || VERBOSE)
        console.log('SplideHandler: received Splide event "moved"');
      const state = this.getNavigationState();
      this.#options.container.dispatchEvent(
        new CustomEvent(SplideHandler.pageChangedEvent, { detail: state }),
      );
      this.#syncLocationHash();
      this.#emitNavigationState();
    });
    this.#splide.on("arrows:updated", () => {
      if (this.#options.verbose || VERBOSE)
        console.log('SplideHandler: received Splide event "arrows:updated"');
      this.#emitNavigationState();
    });
    this.#splide.on("refresh", () => {
      this.#splide.Components.Controller.go(this.#splide.index);
    });
    this.#splide.on(EVENT_LAZYLOAD_LOADED, this.#suspendZoomForMeasurement);
    this.#splide.on(EVENT_RESIZE, this.#restoreZoomAfterMeasurement);
    if (this.#urlHashEnabled) {
      this.#window.addEventListener("hashchange", this.#handleHashChange);
    }
  }

  #suspendZoomForMeasurement = () => {
    const zoomController = this.parent.zoomController;
    if (!zoomController?.isZoomed() || !zoomController.suspendTransform())
      return;

    this.#zoomMeasurementPending = true;

    // Splide emits resize synchronously after lazyload:loaded. Restore in
    // a microtask as a safety net if that implementation detail changes.
    Promise.resolve().then(this.#restoreZoomAfterMeasurement);
  };

  #prepareWindowResizeMeasurement = () => {
    if (this.#windowResizeFrame !== undefined) return;

    this.#windowResizeFrame = globalThis.requestAnimationFrame(() => {
      this.#windowResizeFrame = undefined;
      const zoomController = this.parent.zoomController;
      if (!zoomController?.isZoomed() || !zoomController.suspendTransform())
        return;

      this.#zoomMeasurementPending = true;
    });
  };

  #restoreZoomAfterMeasurement = () => {
    if (!this.#zoomMeasurementPending) return;

    this.#zoomMeasurementPending = false;
    this.parent.zoomController?.restoreTransform();
  };

  #emitNavigationState() {
    const state = this.getNavigationState();
    const serializedState = JSON.stringify(state);
    if (serializedState === this.#lastNavigationState) return;

    this.#lastNavigationState = serializedState;
    this.#options.container.dispatchEvent(
      new CustomEvent(SplideHandler.navigationStateChangedEvent, {
        detail: state,
      }),
    );
  }

  goToFirst() {
    if (this.#options.verbose || VERBOSE)
      console.log("SplideHandler: goToFirst");

    this.#go(0);
  }

  goToPrevious() {
    if (this.#options.verbose || VERBOSE)
      console.log("SplideHandler: goToPrevious");

    this.#go("<");
  }

  goToNext() {
    if (this.#options.verbose || VERBOSE)
      console.log("SplideHandler: goToNext");

    this.#go(">");
  }

  goToLast() {
    if (this.#options.verbose || VERBOSE)
      console.log("SplideHandler: goToLast");

    this.#go(this.#splide.length - 1);
  }

  #go(destination) {
    this.#splide.go(destination);
  }

  refresh() {
    if (this.#options.verbose || VERBOSE) console.log("SplideHandler: refresh");
    this.#splide.refresh();
    this.#emitNavigationState();
  }

  /**
   * Recalculates the list translation for a known page after an outer layout
   * change. Panzoom's transform must not affect Splide's DOM measurements.
   */
  reposition(index = this.#splide.index) {
    if (this.#options.verbose || VERBOSE)
      console.log("SplideHandler: reposition", index);

    const zoomController = this.parent.zoomController;
    const ownsSuspendedTransform = zoomController?.suspendTransform() ?? false;

    try {
      if (index === this.#splide.index) {
        this.#splide.Components.Move.jump(index);
      } else {
        this.#splide.Components.Controller.go(index, true);
      }
    } finally {
      if (ownsSuspendedTransform) zoomController.restoreTransform();
    }

    this.#emitNavigationState();
  }

  #dualPageIndex(pageIndex) {
    // Only the cover needs special treatment: its leading boundary slide
    // belongs to the initial spread. Every other explicit page hash remains
    // the exact first page of the requested desktop view.
    return pageIndex === 1 ? 0 : pageIndex;
  }

  #getLocationHash() {
    return this.#window.location?.hash.replace(/^#/, "") ?? "";
  }

  #findPageIndexByHash(hash) {
    if (!hash) return -1;

    return [...this.#options.root.querySelectorAll(".splide__slide")].findIndex(
      (slide) => slide.dataset.splideHash === hash,
    );
  }

  #getRetainedPageHash() {
    const perPage = this.#isDualPage ? 2 : 1;
    const firstVisibleIndex = this.#splide.index;
    const locationHash = this.#getLocationHash();
    const locationIndex = this.#findPageIndexByHash(locationHash);

    if (
      locationIndex >= firstVisibleIndex &&
      locationIndex < firstVisibleIndex + perPage
    ) {
      return locationHash;
    }

    const slides = this.#options.root.querySelectorAll(".splide__slide");
    for (
      let index = firstVisibleIndex;
      index < Math.min(slides.length, firstVisibleIndex + perPage);
      index += 1
    ) {
      const hash = slides[index]?.dataset.splideHash;
      if (hash) return hash;
    }

    return "";
  }

  #isPageIndexVisible(pageIndex) {
    const perPage = this.#isDualPage ? 2 : 1;
    return (
      pageIndex >= this.#splide.index &&
      pageIndex < this.#splide.index + perPage
    );
  }

  #syncLocationHash() {
    if (!this.#urlHashEnabled) return;

    const preferredIndex = this.#findPageIndexByHash(this.#preferredPageHash);
    if (preferredIndex >= 0 && this.#isPageIndexVisible(preferredIndex)) {
      this.#replaceLocationHash(this.#preferredPageHash);
      return;
    }

    this.#preferredPageHash = "";
    const hash = this.#getRetainedPageHash();
    if (hash) this.#replaceLocationHash(hash);
  }

  #handleHashChange = () => {
    const hash = this.#getLocationHash();
    const pageIndex = this.#findPageIndexByHash(hash);
    if (pageIndex < 0) return;

    this.#preferredPageHash = hash;
    this.#go(this.#isDualPage ? this.#dualPageIndex(pageIndex) : pageIndex);
  };

  #replaceLocationHash(hash) {
    if (!hash || !this.#window.history?.replaceState) return;

    const url = new this.#window.URL(this.#window.location.href);
    url.hash = hash;
    this.#window.history.replaceState(this.#window.history.state, "", url);
    this.#preferredPageHash = hash;
  }

  toDualPage() {
    if (this.#options.verbose || VERBOSE)
      console.log("SplideHandler: toDualPage");
    if (this.#isDualPage) return false;

    const retainedPageHash = this.#getRetainedPageHash();
    const retainedPageIndex = this.#findPageIndexByHash(retainedPageHash);
    // The leading boundary slide shifts every real page one index to the
    // right. Keep that exact page as the desktop start, except for the cover,
    // which belongs after the empty boundary page.
    const targetIndex = this.#dualPageIndex(
      retainedPageIndex >= 0 ? retainedPageIndex + 1 : this.#splide.index + 1,
    );
    const zoomController = this.parent.zoomController;
    const ownsSuspendedTransform = zoomController?.suspendTransform() ?? false;

    try {
      this.#splide.add(
        '<li class="splide__slide"><div class="' +
          this.#classPrefix +
          'page__interior"></div></li>',
        0,
      );
      this.#splide.add(
        '<li class="splide__slide"><div class="' +
          this.#classPrefix +
          'page__interior"></div></li>',
        this.#splide.length,
      );

      this.#isDualPage = true;
      this.#splide.options = { perPage: 2 };
      this.#setAspectRatio();
      this.refresh();
      this.reposition(targetIndex);
      this.#replaceLocationHash(retainedPageHash);
    } finally {
      if (ownsSuspendedTransform) zoomController.restoreTransform();
    }
    return true;
  }

  toSinglePage() {
    if (this.#options.verbose || VERBOSE)
      console.log("SplideHandler: toSinglePage");
    if (!this.#isDualPage) return false;

    const retainedPageHash = this.#getRetainedPageHash();
    const retainedPageIndex = this.#findPageIndexByHash(retainedPageHash);
    // Removing the leading boundary slide shifts every real page one index
    // to the left. Resolve the retained page by its stable hash where
    // possible, rather than trusting a transient carousel index.
    const targetIndex = Math.max(
      0,
      (retainedPageIndex >= 0 ? retainedPageIndex : this.#splide.index) - 1,
    );
    const zoomController = this.parent.zoomController;
    const ownsSuspendedTransform = zoomController?.suspendTransform() ?? false;

    try {
      this.#isDualPage = false;
      this.#splide.options = { perPage: 1 };
      this.#splide.remove([0, this.#splide.length - 1]);

      this.#setAspectRatio();

      this.refresh();
      this.reposition(targetIndex);
      this.#replaceLocationHash(retainedPageHash);
    } finally {
      if (ownsSuspendedTransform) zoomController.restoreTransform();
    }
    return true;
  }

  setDragEnabled(enabled) {
    if (this.#options.verbose || VERBOSE)
      console.log("SplideHandler: setDragEnabled", enabled);
    this.#splide.Components.Drag.disable(!enabled);
  }

  startPinch() {
    if (this.#pinchActive) return false;

    this.#cancelPinchRelease();
    this.#pinchActive = true;
    this.setDragEnabled(false);
    return true;
  }

  endPinch(enableDrag) {
    if (!this.#pinchActive) return false;

    this.#pinchActive = false;
    this.#cancelPinchRelease();
    // Panzoom's pointerup precedes Splide's touchend. Wait until the next
    // frame so Splide can finish and discard its original one-finger drag
    // before page dragging is enabled again.
    this.#pinchReleaseFrame = globalThis.requestAnimationFrame(() => {
      this.#pinchReleaseFrame = undefined;
      this.setDragEnabled(enableDrag);
    });
    return true;
  }

  #blockPinchTouchMove = (event) => {
    if (!this.#pinchActive) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  };

  #cancelPinchRelease() {
    if (this.#pinchReleaseFrame === undefined) return;

    globalThis.cancelAnimationFrame(this.#pinchReleaseFrame);
    this.#pinchReleaseFrame = undefined;
  }

  destroy() {
    if (this.#options.verbose || VERBOSE) console.log("SplideHandler: destroy");
    this.#cancelPinchRelease();
    this.#window.removeEventListener("hashchange", this.#handleHashChange);
    this.#window.removeEventListener(
      "resize",
      this.#prepareWindowResizeMeasurement,
    );
    if (this.#windowResizeFrame !== undefined) {
      globalThis.cancelAnimationFrame(this.#windowResizeFrame);
      this.#windowResizeFrame = undefined;
    }
    this.#restoreZoomAfterMeasurement();
    this.#options.root.removeEventListener(
      "touchmove",
      this.#blockPinchTouchMove,
      true,
    );
    this.#restoreZoomAfterMeasurement();
    this.#splide?.destroy(true);
    this.#splide = undefined;
  }

  #setAspectRatio() {
    if (this.#options.verbose || VERBOSE)
      console.log("SplideHandler: setAspectRatio");

    const firstPageImage = this.#options.root.querySelector("img");
    if (!firstPageImage) return;
    this.pageAspectWidth =
      Number(firstPageImage.getAttribute("width")) ||
      firstPageImage.naturalWidth;
    this.pageAspectHeight =
      Number(firstPageImage.getAttribute("height")) ||
      firstPageImage.naturalHeight;

    if (!this.pageAspectWidth || !this.pageAspectHeight) return;

    const width = this.#options.container.classList.contains(
      SplideHandler.doublePageClass,
    )
      ? this.pageAspectWidth * 2
      : this.pageAspectWidth;
    const aspectRatio = `${width} / ${this.pageAspectHeight}`;

    this.#options.book.style.aspectRatio = aspectRatio;
  }
}
